import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit, viewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import * as XLSX from 'xlsx';

import { ShoppingFacade } from 'ish-core/facades/shopping.facade';
import { SkuQuantityType } from 'ish-core/models/product/product.model';

import {
  QuickOrderError,
  UpdateQuickOrderItem,
} from './../../../custom-quickorder/models/custom-quickorder/custom-quickorder.model';
import { CustomQuickorderFacade } from './../../facades/custom-quickorder.facade';

declare type CsvStatusType = 'Default' | 'ValidFormat' | 'InvalidFormat' | 'IncorrectInput';

@Component({
  selector: 'custom-quickorder-upload',
  templateUrl: './custom-quickorder-upload.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CustomQuickorderUploadComponent implements OnInit {
  csvForm: FormGroup;
  productsFromCsv: SkuQuantityType[] = [];
  status: CsvStatusType;
  quickOrderItems: UpdateQuickOrderItem[];
  quickOrderValidationErrors$: Observable<QuickOrderError[]>;
  csvFileName = new FormControl();
  readonly csvFile = viewChild<ElementRef>('CSVFile');

  constructor(
    private qf: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private shoppingFacade: ShoppingFacade,
    private customQuickorderFacade: CustomQuickorderFacade
  ) {}

  ngOnInit() {
    this.csvForm = this.qf.group({
      csvFile: ['', Validators.required],
    });
    this.quickOrderValidationErrors$ = this.customQuickorderFacade.quickOrderValidations$;
    this.status = 'Default';
  }

  uploadListener(target: EventTarget): void {
    const files = (target as HTMLInputElement).files;
    this.status = 'Default';
    this.csvFileName = new FormControl(files[0].name);
    if (this.isValidCSVFile(files[0])) {
      const reader = new FileReader();
      reader.readAsText(files[0]);
      reader.onload = () => {
        const csvData = reader.result;
        const csvRecordsArray = (csvData as string).split(/\r\n|\n/);
        this.quickOrderItems = this.getDatafromCSVFile(csvRecordsArray);
      };

      reader.onloadend = () => {
        this.quickOrderItems = this.quickOrderItems.filter((_p, index) => index > 0);
        const quickOrderItemsFilter = this.quickOrderItems.filter(
          p => !isNaN(p.quantity) && p.productSKU !== '' && p.quantity !== 0
        );
        if (quickOrderItemsFilter.length !== this.quickOrderItems.length || quickOrderItemsFilter.length === 0) {
          this.status = 'IncorrectInput';
        } else {
          this.status = 'ValidFormat';
        }

        this.cdRef.markForCheck();
      };
    } else if (this.isValidXLSXFile(files[0])) {
      const heading = [['productSKU', 'quantity']];
      const reader = new FileReader();
      reader.readAsBinaryString(files[0]);
      reader.onload = () => {
        const xlsxData = reader.result;
        const wb: XLSX.WorkBook = XLSX.read(xlsxData, { type: 'binary' });
        const wsname: string = wb.SheetNames[0];
        const ws: XLSX.WorkSheet = wb.Sheets[wsname];
        XLSX.utils.sheet_add_aoa(ws, heading);
        this.quickOrderItems = XLSX.utils.sheet_to_json(ws);
      };
      reader.onloadend = () => {
        const quickOrderItemsFilter = this.quickOrderItems.filter(
          p => !isNaN(p.quantity) && p.productSKU !== undefined && p.quantity !== undefined
        );
        if (quickOrderItemsFilter.length !== this.quickOrderItems.length || quickOrderItemsFilter.length === 0) {
          this.status = 'IncorrectInput';
        } else {
          this.status = 'ValidFormat';
        }
        this.cdRef.markForCheck();
      };
    } else {
      this.status = 'InvalidFormat';
    }
  }

  isValidCSVFile(file: File) {
    return file.name.endsWith('.csv');
  }
  isValidXLSXFile(file: File) {
    return file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
  }

  getDataRecordsArrayFromCSVFile(csvRecordsArray: string[]): SkuQuantityType[] {
    try {
      return csvRecordsArray
        .filter(r => !!r)
        .map(record => record.split(/[,;]/))
        .map(record => ({
          sku: record[0].trim(),
          quantity: +record[1].trim(),
        }))
        .filter(record => !isNaN(record.quantity));
    } catch (error) {
      this.status = 'IncorrectInput';
      return [];
    }
  }
  getDatafromCSVFile(csvRecordsArray: string[]): UpdateQuickOrderItem[] {
    try {
      return csvRecordsArray
        .filter(r => !!r)
        .map(record => record.split(/[,;]/))
        .map(record => ({
          productSKU: record[0].trim(),
          quantity: +record[1].trim(),
        }));
    } catch (error) {
      this.status = 'IncorrectInput';
      return [];
    }
  }
  addCsvToCart() {
    if (this.status === 'ValidFormat') {
      if (this.productsFromCsv.length > 0) {
        this.productsFromCsv.forEach(product => {
          this.shoppingFacade.addProductToBasket(product.sku, product.quantity);
        });
      }
      this.resetCsvProductArray();
    }
  }
  uploadQuickOrder() {
    if (this.status === 'ValidFormat') {
      if (this.quickOrderItems.length > 0) {
        this.customQuickorderFacade.createQuickOrderUploadItems(this.quickOrderItems);
      }
      this.resetQuickOrderItems();
    }
  }
  resetQuickOrderItems() {
    this.productsFromCsv = [];
    this.status = 'Default';
    this.csvFileName = new FormControl('');
    this.csvFile().nativeElement.value = '';
  }
  resetCsvProductArray() {
    this.quickOrderItems = [];
    this.status = 'Default';
  }

  get isCsvDisabled() {
    return this.status !== 'ValidFormat';
  }
}
